import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Newspaper, Star, Twitter, Facebook, Link2, Check, BookOpen } from 'lucide-react';
import SwipeCarousel from '../SwipeCarousel';
import PressReleaseReaderModal from './PressReleaseReaderModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

/**
 * PressReleaseSection — public, read-only press-release **link card** carousel
 * on the book detail page (FEAT-008).
 *
 * Each slide is a tappable link-card that renders the source publication's
 * own thumbnail + headline + summary (fetched from the URL's OG tags) and
 * opens the original article in a new tab. No content is uploaded — the
 * source site stays the source of truth.
 *
 * - Hides itself entirely when the book has no saved links.
 * - Single-link case renders the card directly with no carousel chrome.
 * - If one article is admin-pinned as `is_featured`, it renders ABOVE the
 *   carousel as a wider hero strip (and is excluded from the carousel below
 *   so it never shows twice).
 */
const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString();
};

const cleanDomain = (url) => {
  try {
    const u = new URL(url);
    return u.hostname.replace(/^www\./i, '');
  } catch {
    return '';
  }
};

const ShareButtons = ({ article }) => {
  const [copied, setCopied] = useState(false);
  const url = article.external_url;
  const text = article.headline || article.og_title || `Press coverage from ${article.source || cleanDomain(url)}`;
  const enc = encodeURIComponent;
  const twitterHref = `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(url)}`;
  const facebookHref = `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`;

  const onCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        ok = true;
      }
    } catch { /* fall through */ }
    if (!ok) {
      // Fallback for blocked permissions / non-HTTPS / older browsers
      try {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        ok = true;
      } catch { /* swallow */ }
    }
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  const stop = (e) => e.stopPropagation();

  return (
    <div
      className="flex items-center gap-2 mt-4 pt-4 border-t border-white/10"
      onClick={stop}
      data-testid={`press-release-share-${article.id}`}
    >
      <span className="text-xs text-gray-400 mr-1 font-medium uppercase tracking-wider">Share</span>
      <a
        href={twitterHref}
        target="_blank"
        rel="noopener noreferrer"
        onClick={stop}
        className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/5 hover:bg-sky-500/20 text-gray-300 hover:text-sky-400 transition-colors"
        title="Share on X (Twitter)"
        data-testid={`press-share-twitter-${article.id}`}
      >
        <Twitter className="h-4 w-4" />
      </a>
      <a
        href={facebookHref}
        target="_blank"
        rel="noopener noreferrer"
        onClick={stop}
        className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/5 hover:bg-blue-600/20 text-gray-300 hover:text-blue-400 transition-colors"
        title="Share on Facebook"
        data-testid={`press-share-facebook-${article.id}`}
      >
        <Facebook className="h-4 w-4" />
      </a>
      <button
        type="button"
        onClick={onCopy}
        className={`inline-flex items-center justify-center w-8 h-8 rounded-full transition-colors ${
          copied
            ? 'bg-emerald-500/20 text-emerald-400'
            : 'bg-white/5 hover:bg-white/15 text-gray-300 hover:text-white'
        }`}
        title={copied ? 'Link copied!' : 'Copy link'}
        data-testid={`press-share-copy-${article.id}`}
      >
        {copied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
      </button>
      {copied && (
        <span className="text-xs text-emerald-400 ml-1" data-testid={`press-share-copied-${article.id}`}>
          Copied!
        </span>
      )}
    </div>
  );
};

const ArticleCard = ({ article, hero = false, onOpen }) => {
  const heading =
    article.headline || article.og_title || article.source || 'Read article';
  const summary = article.og_description || '';
  const source =
    article.source || article.og_site_name || cleanDomain(article.external_url);
  const image = article.og_image || null;

  // Click handler — opens the in-app reader modal instead of navigating away.
  // Backed by `<button>`s so screen readers + keyboard work correctly. We
  // still expose a tiny "Open original" link in the modal header + footer
  // so the source site stays one tap away.
  const handleOpen = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (e && e.stopPropagation) e.stopPropagation();
    if (onOpen) onOpen(article);
  };

  const cardInner = (
    <article
      className={`bg-white/5 backdrop-blur-sm border ${
        hero
          ? 'border-amber-400/40 hover:border-amber-300/70 ring-1 ring-amber-400/20'
          : 'border-white/10 group-hover:border-fuchsia-400/40'
      } transition-colors rounded-2xl overflow-hidden ${
        hero ? 'md:flex md:items-stretch' : 'sm:flex sm:items-stretch'
      }`}
    >
      {image && (
        <button
          type="button"
          onClick={handleOpen}
          className={`bg-zinc-950 overflow-hidden block flex-shrink-0 cursor-pointer ${
            hero
              ? 'md:w-1/2 aspect-[4/3] md:aspect-auto'
              : 'aspect-[3/2] sm:aspect-auto sm:w-48 md:w-56'
          }`}
          aria-label={`Read: ${heading}`}
          data-testid={`press-release-image-open-${article.id}`}
        >
          <img
            src={image}
            alt=""
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
            className="w-full h-full object-contain transition-transform duration-500 hover:scale-[1.02]"
            onError={(e) => {
              e.currentTarget.parentElement.style.display = 'none';
            }}
          />
        </button>
      )}
      <div className={`p-5 sm:p-6 flex-1 ${hero ? 'md:flex md:flex-col md:justify-center md:p-8' : 'sm:flex sm:flex-col sm:justify-center'}`}>
        {hero && (
          <span
            className="inline-flex items-center gap-1.5 self-start bg-amber-400/20 text-amber-300 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider mb-3"
            data-testid={`press-release-featured-badge-${article.id}`}
          >
            <Star className="h-3 w-3 fill-current" /> Featured Coverage
          </span>
        )}
        <button
          type="button"
          onClick={handleOpen}
          className="block w-full text-left group/link cursor-pointer"
          data-testid={hero ? `press-release-featured-${article.id}` : `press-release-card-${article.id}`}
        >
          <div className="flex items-center gap-2 text-sm text-fuchsia-300 mb-2 flex-wrap">
            <span
              className="font-semibold tracking-wide"
              data-testid={`press-release-source-${article.id}`}
            >
              {source}
            </span>
            {article.publication_date && (
              <>
                <span className="text-fuchsia-300/40">·</span>
                <span className="text-gray-400">{formatDate(article.publication_date)}</span>
              </>
            )}
          </div>
          <h4
            className={`font-bold text-white mb-2.5 leading-snug group-hover/link:text-fuchsia-200 transition-colors ${
              hero ? 'text-xl sm:text-2xl md:text-3xl' : 'text-lg sm:text-xl md:text-2xl'
            }`}
            data-testid={`press-release-headline-${article.id}`}
          >
            {heading}
          </h4>
          {summary && (
            <p
              className={`text-gray-200 leading-relaxed mb-3 ${
                hero ? 'text-sm sm:text-base line-clamp-4' : 'text-sm sm:text-base line-clamp-3'
              }`}
              data-testid={`press-release-summary-${article.id}`}
            >
              {summary}
            </p>
          )}
          <span
            className={`inline-flex items-center gap-1 text-sm ${
              hero
                ? 'text-amber-300 group-hover/link:text-amber-200 font-semibold'
                : 'text-fuchsia-300 group-hover/link:text-fuchsia-200 font-semibold'
            }`}
            data-testid={`press-release-link-${article.id}`}
          >
            <BookOpen className="h-3.5 w-3.5" />
            Read article inside the platform
          </span>
        </button>
        {hero && <ShareButtons article={article} />}
      </div>
    </article>
  );

  if (hero) return cardInner;
  return (
    <div
      className="block group"
      data-testid={`press-release-card-wrap-${article.id}`}
    >
      {cardInner}
    </div>
  );
};

const PressReleaseSection = ({ bookId, refreshKey = 0 }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openArticle, setOpenArticle] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios
      .get(`${API}/books/${bookId}/press-releases`)
      .then((res) => {
        if (mounted) setArticles(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (mounted) setArticles([]);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [bookId, refreshKey]);

  if (loading || articles.length === 0) return null;

  const featured = articles.find((a) => a.is_featured) || null;
  const others = featured ? articles.filter((a) => a.id !== featured.id) : articles;

  const handleOpen = (article) => setOpenArticle(article);
  const handleClose = () => setOpenArticle(null);

  return (
    <section
      className="bg-gradient-to-b from-black/65 via-black/50 to-black/65 backdrop-blur-sm py-12"
      data-testid="press-release-section"
    >
      <div className="max-w-4xl mx-auto px-6">
        <div className="flex items-center gap-2 mb-6">
          <Newspaper className="h-5 w-5 text-fuchsia-400" />
          <h3 className="text-lg font-semibold text-white">Press Coverage</h3>
          {articles.length > 1 && (
            <span className="text-sm text-gray-400 font-normal">({articles.length})</span>
          )}
        </div>

        {featured && (
          <div className="mb-6" data-testid="press-release-featured-strip">
            <ArticleCard article={featured} hero onOpen={handleOpen} />
          </div>
        )}

        {others.length > 0 && (
          <SwipeCarousel
            items={others}
            testId="press-release"
            ariaLabel="Press coverage links"
            renderItem={(article) => (
              <ArticleCard article={article} onOpen={handleOpen} />
            )}
          />
        )}
      </div>

      {openArticle && (
        <PressReleaseReaderModal
          bookId={bookId}
          article={openArticle}
          onClose={handleClose}
        />
      )}
    </section>
  );
};

export default PressReleaseSection;
